import React, { useContext, useEffect, useRef, useState } from "react";
import { useGeoApps } from "@mapgear/geoapps-ui-framework";
import { FeatureInfoContext } from "./FeatureInfoContext";
import { OpacitySlider } from "./OpacitySlider";
import { LayerItemId } from "../types/LayerItemId";
import { LegendLayer } from "./LegendLayer";

export const Layers = (): JSX.Element => {
    const { geoapps } = useGeoApps();
    const { helpLayer, cadastralLayers } = useContext(FeatureInfoContext);
    const [baseLayers, setBaseLayers] = useState<any[]>([]);
    const [activeBaseLayer, setActiveBaseLayer] = useState();
    const [layers, setLayers] = useState<any[]>([]);
    const [otherLayers, setOtherLayers] = useState<any[]>([]);
    const [opacity, setOpacity] = useState<number>(100);
    const [initInteraction, setInitInteraction] = useState<boolean>(false);
    const hoverRef = useRef<HTMLDivElement>(null);
    const map = geoapps.GetMap("map");

    /**
     * Init layers
     */
    useEffect(() => {
        if (!map) return;
        // Get baselayers
        if (baseLayers.length === 0) {
            setBaseLayers(map.BaseLayers.Items);
            setActiveBaseLayer(map.BaseLayers.activeBaselayer);
        }

        // Get layers
        if (layers.length === 0) {
            const plotLayer = map.Layers.Items.filter((layer) => layer.LayerItemId === LayerItemId.PLOTS);
            if (plotLayer.length === 0) return;
            setLayers(plotLayer[0].ChildLayers);
        }

        // Get other layers
        if (otherLayers.length === 0) {
            const other = map.Layers.Items.filter(
                (layer) => layer.LayerItemId !== LayerItemId.PLOTS && layer.LayerItemId !== LayerItemId.HELP,
            );
            if (other.length === 0) return;
            setOtherLayers(other.reverse());
        }

        // Add interactions
        if (layers.length > 0 && !initInteraction) {
            // Add overlay
            const popupOverLay = map.CreateOverlay(hoverRef.current, {
                offset: [0, -53],
                positioning: "bottom-center",
            });

            // Enable hover
            layers[0].EnableHover(true, { overlay: popupOverLay });

            // Get content div from hover popup
            const content = hoverRef?.current?.children[0] as HTMLDivElement;

            // OnHover set text
            layers[0].onHoverEvent.add((e) => {
                if (e.features.length === 0) return;

                const feature = e.features[0].OlFeature;
                content.innerText = feature.getProperties().nr;
            });

            setInitInteraction(true);
        }
    }, [map, layers]);

    /**
     * Sets the opacity for all of the layers
     */
    useEffect(() => {
        cadastralLayers.forEach((layer) => {
            if (layer.get("name") === "kaart BRK") return;
            layer.setOpacity(Number(opacity / 100));
        });

        helpLayer.setOpacity(Number(opacity / 100));
    }, [opacity]);

    /**
     * Sets a new baselayer
     * @param baseLayer The selected baselayer that is to be activated
     */
    const toggleBaseLayers = (baseLayer: any) => {
        setActiveBaseLayer(baseLayer);
        map.BaseLayers.ActivateBaseLayer(baseLayer);
    };

    return (
        <>
            <div ref={hoverRef} className="popup-hover">
                <div className="popup-hover-content"></div>
                <div className="popup-hover-arrow">
                    <div className="inner"></div>
                </div>
            </div>
            <div className="layers">
                <section className="layers-list">
                    {baseLayers.length > 0 && (
                        <div className="layer-group">
                            {baseLayers.map((baseLayer) => {
                                return (
                                    <label>
                                        <div>
                                            <input
                                                onClick={() => toggleBaseLayers(baseLayer)}
                                                type="radio"
                                                name="baselayer"
                                                checked={baseLayer === activeBaseLayer ? true : false}
                                            />
                                            <span> {baseLayer.title}</span>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                    <div className="layers-separator"></div>
                    {cadastralLayers.length > 0 && (
                        <div className="layer-group">
                            {cadastralLayers.map((layer) => {
                                return (
                                    <LegendLayer layer={layer} layerName={layer.get("name")} layerType="cadastral" />
                                );
                            })}
                        </div>
                    )}
                    {otherLayers.length > 0 && (
                        <div className="layer-group">
                            {otherLayers.map((layer) => {
                                return <LegendLayer layer={layer} layerName={layer.Title} layerType="other" />;
                            })}
                        </div>
                    )}
                    <div className="layer-group">
                        <LegendLayer layer={helpLayer} layerName="hulpkaart" layerType="help" />
                    </div>
                    <b>Percelen</b>
                    {layers.length > 0 && (
                        <div className="layer-group">
                            {layers.map((layer) => {
                                return <LegendLayer layer={layer} layerName={layer.Title} layerType="plots" />;
                            })}
                        </div>
                    )}
                </section>
            </div>
            <OpacitySlider setOpacity={setOpacity} />
        </>
    );
};
